import { Inject, Injectable } from '@angular/core';
import { MutationOptions } from '@apollo/client';
import { Store } from '@ngxs/store';

/* graphql */
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';

/* externals */
import get from 'lodash-es/get';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/* locals */
import { Logger } from 'src/app/_core/tools/logger';
import { UserState } from 'src/app/_user/store/user/user.state';
import { getISODate } from 'src/app/utils/date.utils';
import { environment } from 'src/environments/environment';
import { ContextInfosInput, Mutation } from 'src/graphql/generated-types';

type VupMutation = keyof Mutation;

// soit la mutation est obligatoire, soit le raw est obligatoire
type VupMutationOption<T, V> = (
    (Omit<MutationOptions<T, V>, 'mutation'> & { mutation?: DocumentNode, raw: string }) |
    (MutationOptions<T, V> & { raw?: string })) & { mode?: 'fake' | 'default' };

// le package 'apollo-angular/types' n'est plus accessible publiquement.
// Vu que le type 'EmptyObject' est assez rudimentaire, autant le réécrire directement ici
// https://github.com/the-guild-org/apollo-angular/issues/1756
// https://github.com/the-guild-org/apollo-angular/blob/master/packages/apollo-angular/src/types.ts#L3~#L5
export type EmptyObject = {
    [key: string]: any;
};

@Injectable({
    providedIn: 'root'
})
export class GraphqlService {

    protected readonly logger = new Logger('Graphql');
    private lastCallDate: Date = new Date();

    constructor(private apollo: Apollo, private store: Store) {
    }

    private get context(): ContextInfosInput {
        return this.store.selectSnapshot(UserState.userContext);
    }

    static getTypedMutationName<T, V>(option: MutationOptions<T, V>, index = 0): string {
        return get(option, 'mutation.definitions[' + index + '].selectionSet.selections[' + index + '].name.value');
    }

    mutateMultiple<T extends VupMutation, V = EmptyObject>(options: VupMutationOption<EmptyObject, V>): Observable<{ [key in T]: Mutation[key] }> {
        return this.apollo.mutate<any>(this.formatOption(options)).pipe(
            tap(() => this.lastCallDate = new Date()),
            map(res => res?.data)
        );
    }

    mutateOne<T extends VupMutation, V = EmptyObject>(options: VupMutationOption<T, V>): Observable<Mutation[T]> {

        // format option to get mutation name
        const formattedOption = this.formatOption(options);

        if (formattedOption.mutation.definitions.filter(d => d.kind === 'OperationDefinition').length > 1) {
            return throwError(() => new Error(`can't process multiple operation`));
        }
        const typpedMutationName = GraphqlService.getTypedMutationName(formattedOption);

        return this.apollo.mutate(formattedOption).pipe(
            tap(() => this.lastCallDate = new Date()),
            map(result => result.data[typpedMutationName])
        );
    }

    private formatOption<T, V>(options: VupMutationOption<T, V>): MutationOptions<T, V> {
        const formattedOption = {
            ...options,
            mutation: options.raw ? gql`${options.raw}` : options.mutation,
            variables: {
                contexte: {
                    ...this.context,
                    DateDernierAppel: getISODate(this.lastCallDate.toISOString()),
                },
                ...options.variables,
            }
        };

        if (!environment.production) {

            const customMutationName = get(formattedOption, 'mutation.definitions[0].name.value');

            const queryAsString = options.raw || options.mutation.loc.source.body;
            // la gateway IIS bloque les URL trop longues , donc on met l'url qui fonctionne
            // et on remettra `environment.graphql` quand ça marchera

            const optionsASString = JSON.stringify(
                formattedOption.variables,
                (k, v) => (k === '__typename') ? undefined : v,  // remove "__typename" from option
                4);

            let url = environment.graphql
                + '?variables=' + encodeURIComponent(optionsASString)
                + '&document=' + encodeURIComponent(queryAsString);

            // VTH: Essaye de réduire la requête quand les champs dépassent la longueur max du navigateur
            //      Officiellement 2048 chars, mais l'IIS du graphql semble accepter 2106 chars
            //      Réduit l'indentation à un espace et trim les sauts de lignes
            if (url.length > 2100) {
                url = url.replace(/(?:(?:%20)*(?:%0D)?(%0A)(?:%0D)?(?:%20)*)|(%20)(?:%20)+/gm, '$1$2');
            }

            this.logger.groupCollapsed()(customMutationName);
            this.debug(url);
            this.debug(queryAsString); // mutation en raw
            this.debug('\n', optionsASString); // variables en pretty
            console.groupEnd();
        }

        return formattedOption;
    }

    private debug(...args) {
        this.logger.debug()(...args);
    }
}
